import Layout from "../components/layout/Layout";

export default function PrivacyPolicyPage() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: January 2025</p>

        <div className="prose prose-slate max-w-none">
          <p className="text-slate-600 mb-6">
            CheepSweepz Concierge (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) operates as an import facilitation 
            and concierge service. This Privacy Policy explains how we collect, use, and protect your information 
            when you use our services.
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Contact Information</h2>
            <p className="text-slate-600">
              <strong>Business Name:</strong> CheepSweepz Concierge<br />
              <strong>Address:</strong> Culloden Road, Bridgetown, St. Michael, Barbados<br />
              <strong>Phone:</strong> <a href="tel:+12468411248" className="text-orange-600 hover:underline">+1 246-841-1248</a> | <a href="tel:+12464273323" className="text-orange-600 hover:underline">+1 246-427-3323</a><br />
              <strong>Email:</strong> <a href="mailto:cheepsweepz@gmail.com" className="text-orange-600 hover:underline">cheepsweepz@gmail.com</a><br />
              <strong>Hours:</strong> Open 24 hours
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Information We Collect</h2>
            <p className="text-slate-600 mb-4">We collect information necessary to provide our import and delivery services:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Account Details:</strong> Name, email address, and contact information when you create an account.</li>
              <li><strong>Submitted Product Links:</strong> URLs and product information you submit for import requests.</li>
              <li><strong>Order Details:</strong> Shipping addresses, order preferences, and delivery instructions.</li>
              <li><strong>Payment Confirmation Data:</strong> Payment method used and transaction confirmation. We do not store credit card numbers or bank account details directly.</li>
              <li><strong>Help Requests:</strong> Product search requests and communication records.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">2. How We Use Information</h2>
            <p className="text-slate-600 mb-4">Your information is used for:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Order Processing:</strong> Purchasing products on your behalf and managing your orders.</li>
              <li><strong>Customs &amp; Shipping:</strong> Completing customs declarations and coordinating delivery.</li>
              <li><strong>Customer Support:</strong> Responding to inquiries and providing assistance.</li>
              <li><strong>Service Improvements:</strong> Analyzing usage to improve our services.</li>
              <li><strong>Communications:</strong> Sending order updates and service notifications.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Third-Party Services</h2>
            <p className="text-slate-600 mb-4">We work with trusted third-party services to operate our business:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Payment Processing:</strong> PayPal processes payments securely. Your payment information is handled by PayPal under their privacy policy.</li>
              <li><strong>Shipping Carriers:</strong> FedEx, DHL, and other freight partners receive shipping information necessary for delivery.</li>
              <li><strong>Email Provider:</strong> Resend is used to send transactional emails and notifications.</li>
              <li><strong>Marketplaces:</strong> We interact with Amazon, eBay, AliExpress, and other marketplaces on your behalf to purchase products.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Data Security</h2>
            <p className="text-slate-600">
              We implement reasonable administrative, technical, and physical safeguards to protect your personal 
              information. This includes secure connections (HTTPS), encrypted data storage, and access controls. 
              However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Cookies &amp; Analytics</h2>
            <p className="text-slate-600">
              Our website may use cookies to maintain your session and preferences. We may use analytics tools to 
              understand how users interact with our service. You can control cookie settings through your browser.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">6. International Data Transfers</h2>
            <p className="text-slate-600">
              As an international import service, your information may be transferred to and processed in countries 
              other than your country of residence. By using our services, you consent to such transfers. We take 
              steps to ensure your information receives adequate protection.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">7. Your Rights</h2>
            <p className="text-slate-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your account and associated data</li>
              <li>Opt out of marketing communications</li>
            </ul>
            <p className="text-slate-600 mt-4">
              To exercise these rights, contact us at <a href="mailto:cheepsweepz@gmail.com" className="text-orange-600 hover:underline">cheepsweepz@gmail.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Updates to This Policy</h2>
            <p className="text-slate-600">
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an 
              updated revision date. Continued use of our services after changes constitutes acceptance of the 
              updated policy.
            </p>
          </section>

          <section className="mb-8 p-4 bg-slate-50 rounded-lg">
            <p className="text-slate-600 text-sm">
              If you have questions about this Privacy Policy, please contact us at{" "}
              <a href="mailto:cheepsweepz@gmail.com" className="text-orange-600 hover:underline">cheepsweepz@gmail.com</a>.
            </p>
          </section>
        </div>
      </div>
    </Layout>
  );
}
